import React, { useEffect, useRef } from 'react';

import cn from 'classnames';
import TextArea from 'react-textarea-autosize';
import { Message, TextMessage } from '@copilotkit/runtime-client-gql';

import { Button, Spinner, Text } from '@components/common';

import { Generative } from '../agents/main/Generative';
import { MainAgentState } from '../agents/main/types';

import { AssistantMessage } from './AssistantMessage';
import { AttachmentsList } from './AttachmentsList';
import { UserMessage } from './UserMessage';

interface Props {
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (content: string) => void;
  nodeName?: string;
  state: MainAgentState;
  className?: string;
}

export const ChatInterface = ({ messages, isLoading, onSendMessage, nodeName, state, className }: Props) => {
  const chatTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (chatTextAreaRef.current) {
      chatTextAreaRef.current.focus();
    }
  }, []);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className='flex-1 space-y-4 overflow-y-auto p-4'>
        {messages.map((message: TextMessage, index) => (
          <div key={message.id || index}>
            {message.role === 'assistant' ? (
              <AssistantMessage
                message={message.content}
                isGenerating={false}
                isLoading={false}
                rawData={message}
                onThumbsUp={() => {}}
                onThumbsDown={() => {}}
                onCopy={() => {}}
                onRegenerate={() => {}}
              />
            ) : (
              <UserMessage message={message.content} />
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
        {isLoading && (
          <div className='flex items-center space-x-2 text-gray-500'>
            <Spinner className='h-4 w-4' />
            <Text h='400' color='gray-500'>
              Thinking…
            </Text>
          </div>
        )}
      </div>
      <div className='mt-auto border-t p-4'>
        <div className='relative rounded-lg border bg-white'>
          <TextArea
            ref={chatTextAreaRef}
            name='message'
            placeholder='Ask Great Question…'
            className='focus:outline-none relative z-10 w-full resize-none rounded-lg border-0 px-3 py-2 text-sm'
            minRows={2}
            maxRows={4}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const value = e.currentTarget.value.trim();
                if (value) {
                  onSendMessage(value);
                  e.currentTarget.value = '';
                }
              }
            }}
          />
          <div className='mt-2 flex w-full items-center justify-between overflow-hidden px-2 pb-2'>
            <div className='mr-2 min-w-0 flex-1 overflow-hidden'>
              <div className='flex min-w-0 items-center space-x-2'>
                <AttachmentsList attachments={state.attachments} />
              </div>
            </div>
            <Button
              small
              primary
              icon='send'
              className='shrink-0'
              aria-label='Send message'
              onClick={() => {
                if (chatTextAreaRef.current) {
                  const value = chatTextAreaRef.current.value.trim();
                  if (value) {
                    onSendMessage(value);
                    chatTextAreaRef.current.value = '';
                  }
                }
              }}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
